import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import FolderIcon from "@mui/icons-material/Folder";
import { hasJwtPermission } from "../services/ServicesHelpers";
import * as XLSX from "xlsx-js-style";
import {
  Badge,
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { COLORS } from "../assets/theme";
import { IAccount, useAccountsQueryAll } from "../services/AccountService";
import { fetchMonitorById, IMonitorById } from "../services/LiveMonitorService";
import { IAction, IField } from "../types/table/interfaces.type";
import CostBreakdownWithDialog from "./CostBreakdownWithDialog";
import {
  getProviderCurrencyOptions,
  getConnectionProfileCurrencyOptions,
} from "../services/CurrencyService";
import { getServiceProviderCurrencyOptions } from "../services/ServiceProviderService";
import CustomLabel from "./common/CustomLabel";
import { ExpandedDetailsTable } from "./ExpandedDetailsLiveMonitorTable";
import { MessageTrackersDialog } from "./MessageTrackersDialog";
import SearchHeaderContainer from "./common/table/SearchHeaderContainer";
import { ReactComponent as Export } from "../assets/icons/export.svg";
import { formatNumberWithCommas } from "../utils/helpers";

export interface LiveMonitorTableProps<T> {
  data: T[];
  fields: IField<T>[];
  actions?: IAction<T>[];
  onActionClick?: (action: IAction<T>, row: T) => void;
  renderActions?: (row: T) => React.ReactElement[];
  initialPageSize?: number;
  profileIdCheck?: boolean;
  search?: string;
  setSearch?: (value: string) => void;
  searchOnEnter?: boolean;
  buttonConfig?: any[];
  filters?: any;
  isLoading?: boolean;
  isRefreshing?: boolean;
  nextRefreshAt?: number | null;
  headerExtra?: React.ReactNode;
  usedBy?: "live-monitor" | "repricing";
  /** Currency of a row's monetary values, used to group the summary totals. */
  getRowCurrency?: (row: T) => string | undefined;
}

interface ISort<T> {
  field?: keyof T;
  direction?: "asc" | "desc";
}

export interface MonitorRowTotals {
  count: number;
  delivered: number;
  totalCost: number;
  totalRate: number;
  totalProfit: number;
}

/**
 * Row totals rebuilt by summing a details response. The details API is hit per
 * row on demand, so its numbers can be newer than the main monitor response
 * behind the table - these replace the row's own totals rather than waiting for
 * a full main-table refetch.
 */
export interface RefreshedRowTotals {
  count: number;
  delivered: number;
  totalCost: number;
  totalRate: number;
  totalProfit: number;
  totalConvertedRoutingCost?: number;
  totalConvertedMnpCost?: number;
  totalConvertedHlrCost?: number;
}

export interface ExpandedDetails {
  columns: string[];
  rows: any[];
  isLoading?: boolean;
  error?: string;
  /** Totals of the main table row this detail table was expanded from. */
  parentTotals?: MonitorRowTotals;
}

const extractNumericValue = (value: any): number => {
  if (typeof value === "string") {
    return parseFloat(value.replace(/[^0-9.]/g, "")) || 0;
  }
  return typeof value === "number" ? value : 0;
};

const ROW_KEY = "__rowKey";
const GROUP_KEY = "__profileGroup";
const GROUP_SIZE_KEY = "__profileGroupSize";

// Assigns a unique key to each row based on its identity
const assignRowKeys = (rows: any[]): any[] => {
  const seen: Record<string, number> = {};
  return rows.map((row) => {
    const identity = row?.isProfile
      ? `profile:${row.name ?? ""}`
      : `account:${row?.id ?? ""}:${row?.name ?? ""}:${row?.profile ?? ""}`;
    const occurrence = seen[identity] ?? 0;
    seen[identity] = occurrence + 1;
    return {
      ...row,
      [ROW_KEY]: occurrence === 0 ? identity : `${identity}#${occurrence}`,
    };
  });
};

export const getRowKey = (row: any): string => row?.[ROW_KEY] ?? "";

const getGroupKey = (row: any): string => row?.[GROUP_KEY] ?? "";

export const getMonitorRowTotals = (row: any): MonitorRowTotals => {
  const convertedParts = [
    row.totalConvertedRoutingCost,
    row.totalConvertedMnpCost,
    row.totalConvertedHlrCost,
  ].filter((v) => v !== null && v !== undefined);
  const costCell = convertedParts.length
    ? convertedParts.reduce((sum: number, v: any) => sum + Number(v), 0)
    : row.totalCost;

  return {
    count: extractNumericValue(row.count),
    delivered: extractNumericValue(row.delivered),
    totalCost: extractNumericValue(costCell),
    totalRate: extractNumericValue(row.totalRate),
    totalProfit: extractNumericValue(row.totalProfit),
  };
};

const REFRESHED_KEY = "__refreshedFromDetails";

const isRowRefreshed = (row: any): boolean => Boolean(row?.[REFRESHED_KEY]);

/** Rebuilds a row's totals from the per-destination rows of its details. */
const sumDetailsIntoRowTotals = (
  monitors: IMonitorById[],
): RefreshedRowTotals => {
  const sum = (key: keyof IMonitorById) =>
    monitors.reduce((total, monitor) => total + (Number(monitor[key]) || 0), 0);
  // Costs the backend leaves out stay out, so the cost breakdown keeps showing
  // only the parts that actually apply to the row.
  const sumIfAny = (key: keyof IMonitorById) =>
    monitors.some(
      (monitor) => monitor[key] !== null && monitor[key] !== undefined,
    )
      ? sum(key)
      : undefined;

  return {
    count: sum("count"),
    delivered: sum("delivered"),
    totalCost: sum("totalCost"),
    totalRate: sum("totalRate"),
    totalProfit: sum("totalProfit"),
    totalConvertedRoutingCost: sumIfAny("totalConvertedRoutingCost"),
    totalConvertedMnpCost: sumIfAny("totalConvertedMnpCost"),
    totalConvertedHlrCost: sumIfAny("totalConvertedHlrCost"),
  };
};

type MonetaryField = "totalCost" | "totalRate" | "totalProfit";

interface CurrencyTotals {
  currency: string;
  totalCost: number;
  totalRate: number;
  totalProfit: number;
}

interface MonitorSummary {
  totalCount: number;
  byCurrency: CurrencyTotals[];
}

/**
 * Summary amounts are shown as whole numbers, except for amounts that round to
 * nothing (below 1) where the decimals are the only meaningful part.
 */
const formatSummaryAmount = (value: number): string =>
  Math.abs(value) >= 1
    ? formatNumberWithCommas(value, { maximumFractionDigits: 0 })
    : formatNumberWithCommas(value, {
        minimumFractionDigits: 3,
        maximumFractionDigits: 6,
      });

/** Currency suffix of values that arrive already labelled, e.g. "12.5 USD". */
const extractCurrencyLabel = (row: any): string => {
  const labelledValue = [row.totalCost, row.totalRate, row.totalProfit].find(
    (v) => typeof v === "string",
  );
  if (!labelledValue) return "";
  return String(labelledValue)
    .replace(/[0-9.,]/g, "")
    .trim();
};

const calculateTotals = <T,>(
  rows: any[],
  getRowCurrency?: (row: T) => string | undefined,
): MonitorSummary => {
  let totalCount = 0;
  const byCurrency = new Map<string, CurrencyTotals>();

  rows.forEach((row) => {
    const rowTotals = getMonitorRowTotals(row);
    totalCount += rowTotals.count;

    const currency = getRowCurrency?.(row) || extractCurrencyLabel(row) || "";
    const bucket = byCurrency.get(currency) ?? {
      currency,
      totalCost: 0,
      totalRate: 0,
      totalProfit: 0,
    };
    bucket.totalCost += rowTotals.totalCost;
    bucket.totalRate += rowTotals.totalRate;
    bucket.totalProfit += rowTotals.totalProfit;
    byCurrency.set(currency, bucket);
  });

  return {
    totalCount,
    byCurrency: Array.from(byCurrency.values()).sort((a, b) =>
      a.currency.localeCompare(b.currency),
    ),
  };
};

/** Sums of one monetary field, kept per currency and joined with "+". */
const CurrencyTotalsValue: React.FC<{
  byCurrency: CurrencyTotals[];
  field: MonetaryField;
}> = ({ byCurrency, field }) => {
  const nonZero = byCurrency.filter((totals) => totals[field] !== 0);
  const shown = nonZero.length > 0 ? nonZero : byCurrency.slice(0, 1);

  if (shown.length === 0) return <>{formatSummaryAmount(0)}</>;

  return (
    <>
      {shown.map((totals, idx) => (
        <React.Fragment key={totals.currency || idx}>
          {idx > 0 && <span style={{ opacity: 0.5 }}>&nbsp;+&nbsp;</span>}
          {formatSummaryAmount(totals[field])}
          {totals.currency ? (
            <>
              &nbsp;<span style={{ opacity: 0.7 }}>{totals.currency}</span>
            </>
          ) : null}
        </React.Fragment>
      ))}
    </>
  );
};

export function exportToExcel<T>(
  data?: T[],
  fields?: IField<T>[],
  profileMap?: Record<string, { currency?: string; employee?: string }>,
) {
  if (!data || !data.length) return;
  let headers =
    fields?.map((f) => f.header).filter((h) => h !== "Currency") || [];
  let keys =
    fields
      ?.map((f) => f.key)
      .filter((k, idx) => fields[idx].header !== "Currency") || [];

  const excelData = data.map((row) => {
    const rowObj: Record<string, any> = {};
    keys.forEach((key, idx) => {
      let value = (row as any)[key];
      if (value == null) value = "";
      if ((row as any).isProfile) {
        if (key === "currency" && (row as any).currency) {
          return;
        }
        if (
          key === "employee" &&
          ((row as any).employee || (row as any).employeeName)
        ) {
          value = (row as any).employee || (row as any).employeeName;
        }
      }
      rowObj[headers[idx]] = value;
    });
    return rowObj;
  });
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  if (worksheet["!ref"]) {
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let rowIdx = 1; rowIdx <= range.e.r; ++rowIdx) {
      const dataIdx = rowIdx - 1;
      if (data[dataIdx] && (data[dataIdx] as any).isProfile) {
        for (let col = range.s.c; col <= range.e.c; ++col) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: col });
          if (!worksheet[cellAddress]) continue;
          worksheet[cellAddress].s = {
            fill: {
              patternType: "solid",
              fgColor: { rgb: "FFFF00" },
            },
          };
        }
      }
    }
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "LiveMonitor");
  XLSX.writeFile(workbook, "live_monitor_table_export.xlsx");
}

/**
 * Rows whose totals were recomputed from a details fetch are tinted green, to
 * separate "newer than the rest of the table" from the gold profile grouping.
 */
const REFRESHED_ROW_BG = "rgba(76, 175, 80, 0.14)";
const REFRESHED_ROW_HOVER_BG = "rgba(76, 175, 80, 0.22)";
const REFRESHED_ROW_BORDER = "rgba(76, 175, 80, 0.55)";

const PROFILE_ROW_BG = "rgba(163, 148, 85, 0.16)";
const PROFILE_ROW_HOVER_BG = "rgba(163, 148, 85, 0.26)";
const PROFILE_ROW_BORDER = "rgba(163, 148, 85, 0.45)";

const REFRESH_BAR_HEIGHT = 3;
const MIN_REFRESH_INDICATOR_MS = 800;

/**
 * How long the details fetched for an expanded row stay reusable *while the
 * queried date range is still taking traffic*. Within this window, re-expanding
 * the row shows the saved copy instead of calling the details API again; past
 * it the row refetches. For a range that closed before today nothing can change
 * anymore, so this timer does not apply and the copy is kept until the page is
 * reloaded. Either way the cache is dropped when the query itself changes, and
 * for a live range also on any main-table refetch (search / reload / poll).
 */
const DETAILS_CACHE_TTL_MS = 60_000;

/**
 * Whether the queried window still reaches into the current UTC day (or past
 * it). Only such a window can still be taking traffic, so only there do saved
 * details go stale. A window that closed before today is final: its details are
 * kept until the page is reloaded rather than expiring on the TTL.
 */
const isRangeStillLive = (filters: any): boolean => {
  const toDate = filters?.toDate;
  if (!toDate) return true; // open-ended, i.e. up to now
  const end = new Date(toDate).getTime();
  if (Number.isNaN(end)) return true;
  const now = new Date();
  const startOfUtcToday = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return end > startOfUtcToday;
};

/** The filters that define the query, ignoring the cache-busting stamp. */
const getQuerySignature = (filters: any): string => {
  if (!filters) return "";
  const { _forceRefresh, ...query } = filters as Record<string, unknown>;
  return JSON.stringify(query);
};

const RefreshSweepBar: React.FC = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: REFRESH_BAR_HEIGHT,
      zIndex: 4,
      overflow: "hidden",
      borderTopLeftRadius: "10px",
      borderTopRightRadius: "10px",
      backgroundColor: "rgba(163, 148, 85, 0.2)",
      boxShadow: "0 0 8px rgba(163, 148, 85, 0.55)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        width: "45%",
        background: `linear-gradient(90deg, rgba(163, 148, 85, 0) 0%, ${COLORS.GOLD} 50%, rgba(163, 148, 85, 0) 100%)`,
        animation: "liveRefreshSweep 850ms linear infinite",
        "@keyframes liveRefreshSweep": {
          from: { transform: "translateX(-100%)" },
          to: { transform: "translateX(222%)" },
        },
      }}
    />
  </Box>
);

const RefreshCountdownBar: React.FC<{ nextRefreshAt: number }> = ({
  nextRefreshAt,
}) => {
  const durationMs = useMemo(
    () => Math.round(Math.max(0, nextRefreshAt - Date.now()) / 100) * 100,
    [nextRefreshAt],
  );

  if (durationMs <= 0) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: REFRESH_BAR_HEIGHT,
        zIndex: 3,
        overflow: "hidden",
        borderTopLeftRadius: "10px",
        borderTopRightRadius: "10px",
      }}
    >
      <Box
        sx={{
          height: "100%",
          backgroundColor: COLORS.GOLD,
          opacity: 0.4,
          transformOrigin: "left center",
          animation: `liveRefreshCountdown ${durationMs}ms linear forwards`,
          "@keyframes liveRefreshCountdown": {
            from: { transform: "scaleX(0)" },
            to: { transform: "scaleX(1)" },
          },
        }}
      />
    </Box>
  );
};

const LiveMonitorTable = <T,>({
  data,
  fields,
  profileIdCheck = false,
  initialPageSize = 20,
  search = "",
  setSearch,
  searchOnEnter = false,
  buttonConfig,
  filters,
  isLoading,
  isRefreshing = false,
  nextRefreshAt = null,
  headerExtra,
  usedBy = "live-monitor",
  getRowCurrency,
}: LiveMonitorTableProps<T> & { filters?: any }) => {
  const monitorPermission =
    usedBy === "repricing" ? "GET_MONITOR_REPRICE" : "GET_MONITOR";
  const canViewMonitor = hasJwtPermission(monitorPermission);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const removeEmptyValues = (obj: any): any => {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const isEmptyString = typeof value === "string" && value.trim() === "";
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        const isPlainObject =
          typeof value === "object" &&
          value !== null &&
          !(value instanceof Date) &&
          !Array.isArray(value);
        const isEmptyObject = isPlainObject && Object.keys(value).length === 0;
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !isEmptyString &&
          !isEmptyArray &&
          !isEmptyObject
        ) {
          cleaned[key] = value;
        }
      }
    }
    return cleaned;
  };

  const [sort, setSort] = useState<ISort<T>>({
    field: "count" as keyof T,
    direction: "desc",
  });

  /**
   * Saved details only ever go stale while the queried window is still taking
   * traffic. For a window that closed before today they are kept as-is until
   * the page is reloaded.
   */
  const rangeIsLive = useMemo(() => isRangeStillLive(filters), [filters]);
  const querySignature = useMemo(() => getQuerySignature(filters), [filters]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedDetails, setExpandedDetails] = useState<
    Record<string, ExpandedDetails | null>
  >({});
  /** When each row's details were fetched; absent means "not cached anymore". */
  const [detailsFetchedAt, setDetailsFetchedAt] = useState<
    Record<string, number>
  >({});
  /**
   * Totals taken from a row's details response. Unlike the cached details these
   * are not on a timer: the details numbers stay the best available ones until
   * the main table actually refetches.
   */
  const [refreshedRows, setRefreshedRows] = useState<
    Record<string, RefreshedRowTotals>
  >({});

  const dropExpiredDetails = useCallback(() => {
    const cutoff = Date.now() - DETAILS_CACHE_TTL_MS;
    setDetailsFetchedAt((prev) => {
      const kept = Object.entries(prev).filter(([, at]) => at > cutoff);
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
  }, []);

  /**
   * A main-table refetch makes every open detail table stale, so the rows are
   * collapsed and their saved copies dropped. Nothing is re-requested: the
   * details API only runs when the user expands a row again.
   */
  const resetExpandedDetails = useCallback(() => {
    setExpandedRows((prev) => (prev.size ? new Set<string>() : prev));
    setExpandedDetails((prev) => (Object.keys(prev).length ? {} : prev));
    setDetailsFetchedAt((prev) => (Object.keys(prev).length ? {} : prev));
    // The incoming main-table response is authoritative again.
    setRefreshedRows((prev) => (Object.keys(prev).length ? {} : prev));
  }, []);

  // Expire cached details on their own schedule, so the "cached" indicator
  // clears itself without waiting for the next render. A closed window has
  // nothing to expire into, so no timer runs for it.
  useEffect(() => {
    if (!rangeIsLive) return;
    const timestamps = Object.values(detailsFetchedAt);
    if (timestamps.length === 0) return;
    const oldest = Math.min(...timestamps);
    const timeout = setTimeout(
      dropExpiredDetails,
      Math.max(0, oldest + DETAILS_CACHE_TTL_MS - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [detailsFetchedAt, dropExpiredDetails, rangeIsLive]);

  /** Rows whose saved details are still reusable. */
  const cachedDetailKeys = useMemo(() => {
    const cutoff = Date.now() - DETAILS_CACHE_TTL_MS;
    return new Set(
      Object.entries(detailsFetchedAt)
        .filter(([, at]) => !rangeIsLive || at > cutoff)
        .map(([rowKey]) => rowKey),
    );
  }, [detailsFetchedAt, rangeIsLive]);

  // Every refetch (search, manual reload or a live poll) hands over a new
  // filters object, so this runs once per refetch.
  const previousQuerySignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousQuerySignatureRef.current;
    previousQuerySignatureRef.current = querySignature;
    // A different query asks a different question - what was saved for the old
    // one cannot answer it.
    if (previous !== null && previous !== querySignature) {
      resetExpandedDetails();
      return;
    }
    // Same query re-run: only a window still taking traffic can have changed,
    // so a closed window keeps its open rows and their saved details.
    if (rangeIsLive) {
      resetExpandedDetails();
    }
  }, [filters, querySignature, rangeIsLive, resetExpandedDetails]);

  const isMainTableFetching = Boolean(isRefreshing || isLoading);
  const wasMainTableFetchingRef = useRef(false);
  useEffect(() => {
    if (
      isMainTableFetching &&
      !wasMainTableFetchingRef.current &&
      rangeIsLive
    ) {
      resetExpandedDetails();
    }
    wasMainTableFetchingRef.current = isMainTableFetching;
  }, [isMainTableFetching, rangeIsLive, resetExpandedDetails]);
  const [selectedProfileParams, setSelectedProfileParams] = useState<{
    id: number;
    type: string;
  } | null>(null);
  const [selectedRowName, setSelectedRowName] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogParams, setDialogParams] = useState<any>({
    connectionProfileId: null,
    vendorId: null,
    operatorId: null,
    type: "CLIENT",
    filters: {},
  });
  /**
   * The table's rows with their keys assigned, and with the totals of any row
   * that has been expanded replaced by the fresher ones from its details. Doing
   * it here means the profile group headers and the summary bar add up the
   * refreshed numbers too, instead of the ones the main table was built with.
   */
  const keyedRows = useMemo(() => {
    const keyed = assignRowKeys(data);
    if (Object.keys(refreshedRows).length === 0) return keyed;
    return keyed.map((row) => {
      const refreshed = refreshedRows[getRowKey(row)];
      return refreshed ? { ...row, ...refreshed, [REFRESHED_KEY]: true } : row;
    });
  }, [data, refreshedRows]);

  const summary = useMemo(
    () => calculateTotals<T>(keyedRows, getRowCurrency),
    [keyedRows, getRowCurrency],
  );

  const hasRefreshedRows = Object.keys(refreshedRows).length > 0;
  const [selectedClientName, setSelectedClientName] = useState<string>("");
  const [selectedProviderName, setSelectedProviderName] = useState<string>("");

  const [selectedType, setSelectedType] = useState<string>("CLIENT");

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  const [showRefreshBar, setShowRefreshBar] = useState(false);
  const refreshStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isRefreshing) {
      refreshStartedAtRef.current = Date.now();
      setShowRefreshBar(true);
      return;
    }
    if (!showRefreshBar) return;
    const elapsed = Date.now() - (refreshStartedAtRef.current ?? 0);
    const remaining = MIN_REFRESH_INDICATOR_MS - elapsed;
    if (remaining <= 0) {
      setShowRefreshBar(false);
      return;
    }
    const timeout = setTimeout(() => setShowRefreshBar(false), remaining);
    return () => clearTimeout(timeout);
  }, [isRefreshing, showRefreshBar]);

  const sortedData = useMemo(() => {
    const { field, direction } = sort;
    const sortValue = fields.find((f) => f.key === field)?.sortValue;
    const valueOf = (row: any) =>
      sortValue ? sortValue(row as T) : row[field as any];

    const compareRows = (a: any, b: any) => {
      const aVal = valueOf(a);
      const bVal = valueOf(b);
      if (aVal == null)
        return bVal != null ? (direction === "asc" ? -1 : 1) : 0;
      if (bVal == null) return direction === "asc" ? 1 : -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    };

    if (profileIdCheck) {
      const grouped = new Map<string, any[]>();
      for (const entry of keyedRows) {
        const profileName = (entry as any).profile || "unknown";
        const bucket = grouped.get(profileName);
        if (bucket) {
          bucket.push(entry);
        } else {
          grouped.set(profileName, [entry]);
        }
      }

      const sum = (rows: any[], key: string) =>
        rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
      const sumIfAny = (rows: any[], key: string) =>
        rows.some((row) => row[key] !== null && row[key] !== undefined)
          ? sum(rows, key)
          : undefined;

      const headers = [...grouped.entries()].map(([profileName, accounts]) => {
        const count = sum(accounts, "count");
        const delivered = sum(accounts, "delivered");
        const employee =
          accounts[0]?.employee || accounts[0]?.employeeName || "";
        return {
          isProfile: true,
          [ROW_KEY]: `profile:${profileName}`,
          [REFRESHED_KEY]: accounts.some(isRowRefreshed),
          [GROUP_KEY]: profileName,
          [GROUP_SIZE_KEY]: accounts.length,
          name: profileName,
          profile: profileName,
          employee,
          employeeName: employee,
          count,
          delivered,
          deliveryPercentage: count > 0 ? (delivered / count) * 100 : 0,
          deliveryRate: count > 0 ? (delivered / count).toFixed(2) : "0.00",
          totalCost: sum(accounts, "totalCost"),
          totalRate: sum(accounts, "totalRate"),
          totalProfit: sum(accounts, "totalProfit"),
          totalConvertedRoutingCost: sumIfAny(
            accounts,
            "totalConvertedRoutingCost",
          ),
          totalConvertedMnpCost: sumIfAny(accounts, "totalConvertedMnpCost"),
          totalConvertedHlrCost: sumIfAny(accounts, "totalConvertedHlrCost"),
        };
      });

      const rows: any[] = [];
      for (const header of field ? [...headers].sort(compareRows) : headers) {
        const accounts = (grouped.get(header[GROUP_KEY]) ?? []).map(
          (account) => ({ ...account, [GROUP_KEY]: header[GROUP_KEY] }),
        );
        rows.push(header, ...(field ? accounts.sort(compareRows) : accounts));
      }

      return rows;
    }
    if (!field) return keyedRows;
    return [...keyedRows].sort(compareRows);
  }, [keyedRows, sort, profileIdCheck, fields]);

  useEffect(() => {
    const liveKeys = new Set(sortedData.map((row) => getRowKey(row)));
    setExpandedRows((prev) => {
      const next = new Set([...prev].filter((key) => liveKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
    setExpandedDetails((prev) => {
      const kept = Object.entries(prev).filter(([key]) => liveKeys.has(key));
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
    setDetailsFetchedAt((prev) => {
      const kept = Object.entries(prev).filter(([key]) => liveKeys.has(key));
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
    setRefreshedRows((prev) => {
      const kept = Object.entries(prev).filter(([key]) => liveKeys.has(key));
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
  }, [sortedData]);

  const groupNames = useMemo(
    () =>
      profileIdCheck
        ? sortedData
            .filter((row: any) => row.isProfile)
            .map((row: any) => getGroupKey(row))
        : [],
    [sortedData, profileIdCheck],
  );

  const allGroupsExpanded =
    groupNames.length > 0 &&
    groupNames.every((group) => expandedGroups.has(group));

  const toggleAllGroups = useCallback(() => {
    setExpandedGroups((prev) => {
      const everyExpanded =
        groupNames.length > 0 && groupNames.every((group) => prev.has(group));
      return everyExpanded ? new Set<string>() : new Set(groupNames);
    });
  }, [groupNames]);

  const visibleRows = useMemo(() => {
    if (!profileIdCheck) return sortedData;
    return sortedData.filter(
      (row: any) => row.isProfile || expandedGroups.has(getGroupKey(row)),
    );
  }, [sortedData, profileIdCheck, expandedGroups]);

  const handleSort = useCallback((field: keyof T) => {
    setSort((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const {
    data: allAccountsData,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = useAccountsQueryAll({});

  console.log("All accounts data loaded:", {
    allAccountsData,
    isLoadingAccounts,
    accountsError,
    hasContent: !!allAccountsData?.content,
    contentLength: allAccountsData?.content?.length,
  });

  const accountsList = Array.isArray(allAccountsData)
    ? allAccountsData
    : allAccountsData?.content || [];

  const transformMonitorForExpandedDetails = useCallback(
    async (
      monitor: IMonitorById & Record<string, any>,
      isClient: boolean,
      selectedRowName: string,
    ) => {
      const deliveryPercentage =
        monitor.count && monitor.count > 0
          ? ((monitor.delivered / monitor.count) * 100).toFixed(2) + "%"
          : "0%";

      const toNumberOrNull = (value: any): number | null =>
        value === null || value === undefined || value === ""
          ? null
          : Number(value);

      const sumOrNull = (...values: Array<number | null | undefined>) => {
        const defined = values.filter((v) => v !== null && v !== undefined);
        if (defined.length === 0) return null;
        return defined.reduce((sum: number, v) => sum + Number(v), 0);
      };

      const displayOrDash = (value: number | string | null | undefined) =>
        value === null || value === undefined ? "-" : value;

      const costValue = toNumberOrNull(monitor.cost);
      const rateValue = toNumberOrNull(monitor.rate);
      const totalCost = toNumberOrNull(monitor.totalCost);
      const totalRate = toNumberOrNull(monitor.totalRate);

      const profitValue = toNumberOrNull(monitor.profit);
      const totalProfit = toNumberOrNull(monitor.totalProfit);

      const clientName = isClient
        ? selectedRowName
        : monitor.relativeConnectionProfileName || "";
      const providerName = isClient
        ? monitor.relativeConnectionProfileName || ""
        : selectedRowName;
      const dialogType = isClient ? "CLIENT" : "VENDOR";

      const clientAccount = accountsList?.find(
        (acc: IAccount) => acc.name === clientName,
      );
      const vendorAccount = accountsList?.find(
        (acc: IAccount) => acc.name === providerName,
      );

      const [
        routingCostCurrency,
        mnpCostCurrency,
        hlrCostCurrency,
        convertedCostCurrency,
      ] = await Promise.all([
        vendorAccount?.id
          ? queryClient
              .fetchQuery(getProviderCurrencyOptions(String(vendorAccount.id)))
              .catch(() => undefined)
          : Promise.resolve(undefined),
        monitor.mnpProviderId
          ? queryClient
              .fetchQuery(
                getServiceProviderCurrencyOptions(monitor.mnpProviderId),
              )
              .catch(() => undefined)
          : Promise.resolve(undefined),
        monitor.hlrProviderId
          ? queryClient
              .fetchQuery(
                getServiceProviderCurrencyOptions(monitor.hlrProviderId),
              )
              .catch(() => undefined)
          : Promise.resolve(undefined),
        clientAccount?.id
          ? queryClient
              .fetchQuery(getConnectionProfileCurrencyOptions(clientAccount.id))
              .catch(() => undefined)
          : Promise.resolve(undefined),
      ]);

      const handleFolderClick = () => {
        const connectionProfileId = clientAccount?.id;
        const vendorId = vendorAccount?.id;
        const isOperatorMissing =
          !monitor.operatorId ||
          !monitor.operatorName ||
          (monitor as any).vendorId ||
          String(monitor.operatorId) === "N/A" ||
          monitor.operatorName === "N/A";
        const isVendorNotAvailable =
          monitor.relativeConnectionProfileName === "N/A";
        const isOperatorNotAvailable =
          String(monitor.operatorId) === "N/A" ||
          monitor.operatorName === "N/A";
        const rawParams: any = {
          ...(filters || {}),
          connectionProfileId,
          vendorId,
          type: dialogType,
          ...(isOperatorNotAvailable
            ? {}
            : {
                destinationOperatorId: monitor.operatorId,
                operatorName: monitor.operatorName,
              }),
          convertedRoutingCost: monitor.convertedRoutingCost,
          ...(isVendorNotAvailable && !monitor.originalRoutingCost
            ? {}
            : { routingCost: monitor.originalRoutingCost }),
          rate: monitor.rate,
          ...(isOperatorMissing && !isVendorNotAvailable
            ? { status: "FAILED" }
            : {}),
          ...(isVendorNotAvailable
            ? { statusIn: "FAILED,ERROR", filterForNullVendorId: true }
            : {}),
          ...(isOperatorNotAvailable ? { filterForNullOperatorId: true } : {}),
          ...(monitor.delivered === 0
            ? { deliveryStatNotIn: "DELIVRD" }
            : { settledOnly: true }),
          ...(monitor.deliveredPercentage === 100.0
            ? { deliveryStat: "DELIVRD" }
            : {}),
          ...(!isVendorNotAvailable &&
          !isOperatorNotAvailable &&
          monitor.delivered === 0 &&
          monitor.ratePerDelivery !== undefined &&
          monitor.ratePerDelivery !== null
            ? { ratePerDelivery: monitor.ratePerDelivery }
            : {}),
          ...(!isVendorNotAvailable &&
          !isOperatorNotAvailable &&
          monitor.delivered === 0 &&
          monitor.costPerDelivery !== undefined &&
          monitor.costPerDelivery !== null
            ? { costPerDelivery: monitor.costPerDelivery }
            : {}),
        };

        if (rawParams.fromDate instanceof Date) {
          rawParams.fromDate = rawParams.fromDate.toISOString();
        }
        if (rawParams.toDate instanceof Date) {
          rawParams.toDate = rawParams.toDate.toISOString();
        }
        const params = removeEmptyValues(rawParams);
        setDialogParams(params);
        setOpenDialog(true);
      };

      const folderPermitted = hasJwtPermission(
        usedBy === "live-monitor"
          ? "GET_ALL_CDRS_LIVE_MONITOR"
          : "GET_ALL_CDRS_REPRICE",
      );

      const convertedCostBreakdown = (
        <CostBreakdownWithDialog
          routingCost={monitor.originalRoutingCost ?? 0}
          routingCostCurrency={routingCostCurrency}
          {...(monitor.originalMnpCost !== undefined &&
          monitor.originalMnpCost !== null
            ? {
                mnpCost: monitor.originalMnpCost,
                mnpCostCurrency: mnpCostCurrency,
              }
            : {})}
          {...(monitor.originalHlrCost !== undefined &&
          monitor.originalHlrCost !== null
            ? {
                hlrCost: monitor.originalHlrCost,
                hlrCostCurrency: hlrCostCurrency,
              }
            : {})}
          {...(monitor.convertedRoutingCost !== undefined &&
          monitor.convertedRoutingCost !== null
            ? { convertedRoutingCost: monitor.convertedRoutingCost }
            : {})}
          {...(monitor.convertedMnpCost !== undefined &&
          monitor.convertedMnpCost !== null
            ? { convertedMnpCost: monitor.convertedMnpCost }
            : {})}
          {...(monitor.convertedHlrCost !== undefined &&
          monitor.convertedHlrCost !== null
            ? { convertedHlrCost: monitor.convertedHlrCost }
            : {})}
          convertedCostCurrency={convertedCostCurrency}
          {...(monitor.mnpProviderName
            ? { mnpProviderName: monitor.mnpProviderName }
            : {})}
          {...(monitor.hlrProviderName
            ? { hlrProviderName: monitor.hlrProviderName }
            : {})}
        />
      );

      const totalConvertedCostBreakdown = (
        <CostBreakdownWithDialog
          routingCost={monitor.totalOriginalRoutingCost ?? 0}
          routingCostCurrency={routingCostCurrency}
          {...(monitor.totalOriginalMnpCost !== undefined &&
          monitor.totalOriginalMnpCost !== null
            ? {
                mnpCost: monitor.totalOriginalMnpCost,
                mnpCostCurrency: mnpCostCurrency,
              }
            : {})}
          {...(monitor.totalOriginalHlrCost !== undefined &&
          monitor.totalOriginalHlrCost !== null
            ? {
                hlrCost: monitor.totalOriginalHlrCost,
                hlrCostCurrency: hlrCostCurrency,
              }
            : {})}
          {...(monitor.totalConvertedRoutingCost !== undefined &&
          monitor.totalConvertedRoutingCost !== null
            ? { convertedRoutingCost: monitor.totalConvertedRoutingCost }
            : {})}
          {...(monitor.totalConvertedMnpCost !== undefined &&
          monitor.totalConvertedMnpCost !== null
            ? { convertedMnpCost: monitor.totalConvertedMnpCost }
            : {})}
          {...(monitor.totalConvertedHlrCost !== undefined &&
          monitor.totalConvertedHlrCost !== null
            ? { convertedHlrCost: monitor.totalConvertedHlrCost }
            : {})}
          convertedCostCurrency={convertedCostCurrency}
          {...(monitor.mnpProviderName
            ? { mnpProviderName: monitor.mnpProviderName }
            : {})}
          {...(monitor.hlrProviderName
            ? { hlrProviderName: monitor.hlrProviderName }
            : {})}
        />
      );

      return {
        "": (
          <IconButton
            onClick={folderPermitted ? handleFolderClick : undefined}
            disabled={!folderPermitted}
          >
            <FolderIcon
              sx={{ color: folderPermitted ? COLORS.GOLD : "#D3D3D3" }}
            />
          </IconButton>
        ),
        ClientName: isClient
          ? selectedRowName
          : monitor.relativeConnectionProfileName || "",
        ProviderName: isClient
          ? monitor.relativeConnectionProfileName || ""
          : selectedRowName,
        Country: monitor.countryName || "",
        Operator: monitor.operatorName || "",
        Count: displayOrDash(monitor.count),
        Delivered: displayOrDash(monitor.delivered),
        "Delivered %": deliveryPercentage,
        "Converted Cost": convertedCostBreakdown,
        convertedCostValue: displayOrDash(
          sumOrNull(
            monitor.convertedRoutingCost,
            monitor.convertedMnpCost,
            monitor.convertedHlrCost,
          ),
        ),
        costValue: displayOrDash(
          sumOrNull(
            monitor.originalRoutingCost,
            monitor.originalMnpCost,
            monitor.originalHlrCost,
          ) ?? costValue,
        ),
        status: monitor.status,
        Rate: displayOrDash(rateValue),
        Profit: displayOrDash(profitValue),
        "Total Converted Cost": totalConvertedCostBreakdown,
        totalConvertedCostValue: displayOrDash(
          sumOrNull(
            monitor.totalConvertedRoutingCost,
            monitor.totalConvertedMnpCost,
            monitor.totalConvertedHlrCost,
          ),
        ),
        totalCostValue: displayOrDash(
          sumOrNull(
            monitor.totalOriginalRoutingCost,
            monitor.totalOriginalMnpCost,
            monitor.totalOriginalHlrCost,
          ) ?? totalCost,
        ),
        TotalRate: displayOrDash(totalRate),
        TotalProfit: displayOrDash(totalProfit),
        ...(monitor.mnpProviderName
          ? { "MNP Provider": monitor.mnpProviderName }
          : {}),
        ...(monitor.hlrProviderName
          ? { "HLR Provider": monitor.hlrProviderName }
          : {}),
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      accountsList,
      filters,
      queryClient,
      usedBy,
      setDialogParams,
      setOpenDialog,
    ],
  );

  const fetchExpandedDetails = useCallback(
    async (
      profileParams: typeof selectedProfileParams,
      rowName: string,
      filters: any,
      rowKey: string,
      parentTotals: MonitorRowTotals,
    ) => {
      if (!profileParams) {
        setExpandedDetails((prev) => ({ ...prev, [rowKey]: null }));
        return;
      }
      try {
        setExpandedDetails((prev) => ({
          ...prev,
          [rowKey]: { columns: [], rows: [], isLoading: true, parentTotals },
        }));
        const rawParams = {
          profileType: profileParams.type,
          ...(filters || {}),
        };
        const params = removeEmptyValues(rawParams);
        const monitors = await fetchMonitorById(params, profileParams.id);
        const isClient = profileParams.type === "CLIENT";
        const hasMnpProvider = monitors.some((m) => m.mnpProviderName);
        const hasHlrProvider = monitors.some((m) => m.hlrProviderName);
        const columns = [
          "",
          "ClientName",
          "ProviderName",
          "Country",
          "Operator",
          "Count",
          "Delivered",
          "Delivered %",
          "Converted Cost",
          "Rate",
          "Profit",
          "Total Converted Cost",
          "TotalRate",
          "TotalProfit",
        ];
        const rows = await Promise.all(
          monitors.map((monitor) =>
            transformMonitorForExpandedDetails(monitor, isClient, rowName),
          ),
        );
        setExpandedDetails((prev) => ({
          ...prev,
          [rowKey]: {
            columns,
            rows: [...rows],
            isLoading: false,
            parentTotals,
          },
        }));
        setDetailsFetchedAt((prev) => ({ ...prev, [rowKey]: Date.now() }));
        // The details were fetched just now, so their totals beat whatever the
        // main table last reported for this row.
        setRefreshedRows((prev) => ({
          ...prev,
          [rowKey]: sumDetailsIntoRowTotals(monitors),
        }));
      } catch (error: any) {
        console.error("Error fetching monitor details:", error);
        setExpandedDetails((prev) => ({
          ...prev,
          [rowKey]: {
            columns: [],
            rows: [],
            isLoading: false,
            error: String(error),
            parentTotals,
          },
        }));
        // A failed attempt is never reused - reopening the row retries.
        setDetailsFetchedAt((prev) => {
          if (!(rowKey in prev)) return prev;
          const { [rowKey]: _dropped, ...rest } = prev;
          return rest;
        });
      }
    },
    [transformMonitorForExpandedDetails],
  );

  const handleRowExpand = useCallback(
    (rowKey: string, row: T) => {
      setExpandedRows((prev) => {
        const updated = new Set(prev);
        const rowName = (row as any).name || "";
        const parentTotals = getMonitorRowTotals(row);

        if (updated.has(rowKey)) {
          updated.delete(rowKey);
        } else {
          updated.add(rowKey);

          // Reuse the saved details while they are still fresh, and never
          // stack a second request on top of one already in flight.
          const cached = expandedDetails[rowKey];
          const fetchedAt = detailsFetchedAt[rowKey];
          const isStillFresh =
            fetchedAt !== undefined &&
            (!rangeIsLive || Date.now() - fetchedAt < DETAILS_CACHE_TTL_MS);
          if (cached?.isLoading || (cached && isStillFresh)) {
            return updated;
          }

          const accountId = (row as any).id;
          const accountType = (row as any).type;

          if (typeof accountId !== "undefined" && accountType) {
            const profileParams = {
              id: Number(accountId),
              type: accountType,
            };
            console.log("Using direct account info:", {
              accountId,
              accountType,
              rowName,
            });
            fetchExpandedDetails(
              profileParams,
              rowName,
              filters,
              rowKey,
              parentTotals,
            );
          } else if (accountsList && accountsList.length > 0) {
            console.log("Looking up account:", {
              rowName,
              totalAccounts: accountsList.length,
            });

            const account = accountsList.find(
              (acc: IAccount) => acc.name === rowName,
            );

            if (account) {
              const profileParams = {
                id: Number(account.id),
                type: account.type || "CLIENT",
              };
              console.log("Found account, fetching details:", profileParams);
              fetchExpandedDetails(
                profileParams,
                rowName,
                filters,
                rowKey,
                parentTotals,
              );
            } else {
              setExpandedDetails((prev) => ({
                ...prev,
                [rowKey]: null,
              }));
              console.error(
                "Account not found for row:",
                rowName,
                "Available accounts:",
                accountsList.map((a: IAccount) => a.name),
              );
            }
          } else {
            setExpandedDetails((prev) => ({
              ...prev,
              [rowKey]: null,
            }));
            console.warn("Accounts data not available when expanding row", {
              hasAllAccountsData: !!allAccountsData,
              hasAccountsList: !!accountsList,
              accountsListLength: accountsList?.length,
            });
          }
        }
        return updated;
      });
    },
    [
      fetchExpandedDetails,
      filters,
      accountsList,
      expandedDetails,
      detailsFetchedAt,
      rangeIsLive,
      allAccountsData,
    ],
  );

  /**
   * Expand toggle. A gold dot marks rows whose details are already saved -
   * opening them shows that copy instead of hitting the details API, until the
   * copy expires and the icon goes back to normal.
   */
  const renderExpandButton = (rowKey: string, isRefreshed = false) => {
    const isExpanded = expandedRows.has(rowKey);
    const isCached = cachedDetailKeys.has(rowKey);
    const tooltipLines = [
      isRefreshed
        ? "This row's totals come from its details, fetched later than the rest of the table."
        : "",
      isCached
        ? rangeIsLive
          ? `Details already loaded - shown from the saved copy (refreshes after ${Math.round(
              DETAILS_CACHE_TTL_MS / 1000,
            )}s)`
          : "Details already loaded - shown from the saved copy"
        : "",
    ].filter(Boolean);
    const icon = (
      <FindInPageIcon
        sx={{
          color: !canViewMonitor
            ? "#D3D3D3"
            : isExpanded || isCached
              ? "#A39455"
              : "inherit",
        }}
      />
    );
    return (
      <Tooltip
        title={
          canViewMonitor && tooltipLines.length > 0 ? (
            <Box sx={{ display: "grid", gap: 0.5 }}>
              {tooltipLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </Box>
          ) : (
            ""
          )
        }
        arrow
      >
        <span>
          <IconButton size="small" disabled={!canViewMonitor}>
            {isCached ? (
              <Badge
                variant="dot"
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: COLORS.GOLD,
                    minWidth: 6,
                    height: 6,
                  },
                }}
              >
                {icon}
              </Badge>
            ) : (
              icon
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch ?? (() => {})}
            searchOnEnter={searchOnEnter}
            buttonConfig={
              buttonConfig ?? [
                {
                  size: "small",
                  bgColor: COLORS.GOLD,
                  startIcon: <Export />,
                  onClick: () => exportToExcel(sortedData, fields),
                  textColor: COLORS.iconColor,
                  label: "Export",
                  minHeight: isSmallScreen ? 44 : undefined,
                  minWidth: isSmallScreen ? 96 : undefined,
                },
              ]
            }
            headerExtra={headerExtra}
          />
        </Box>
      </Box>
      {hasRefreshedRows && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.75,
            px: { xs: 0.5, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "4px",
              flexShrink: 0,
              backgroundColor: REFRESHED_ROW_BG,
              border: `1px solid ${REFRESHED_ROW_BORDER}`,
            }}
          />
          <Typography variant="caption" sx={{ color: COLORS.defaultGray }}>
            Highlighted rows were updated from their details and are newer than
            the rest of the table.
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          position: "relative",
          width: { xs: "auto", sm: "100%" },
          mx: { xs: -2, sm: 0 },
        }}
      >
        {!showRefreshBar && nextRefreshAt ? (
          <RefreshCountdownBar
            key={nextRefreshAt}
            nextRefreshAt={nextRefreshAt}
          />
        ) : null}
        <Fade
          in={showRefreshBar}
          timeout={{ enter: 100, exit: 300 }}
          unmountOnExit
        >
          <Box>
            <RefreshSweepBar />
          </Box>
        </Fade>
        <TableContainer
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            overflowX: "auto",
            overflowY: "visible",
            width: "100%",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: "40px",
                    fontWeight: "bold",
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#fff",
                    py:
                      profileIdCheck && groupNames.length > 0 ? 0.5 : undefined,
                  }}
                >
                  {profileIdCheck && groupNames.length > 0 ? (
                    <Tooltip
                      title={allGroupsExpanded ? "Collapse all" : "Expand all"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={toggleAllGroups}
                        sx={{ p: 0.25 }}
                      >
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          sx={{
                            color: COLORS.GOLD,
                            transition: "transform 0.2s ease",
                            transform: allGroupsExpanded
                              ? "rotate(0deg)"
                              : "rotate(-90deg)",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>

                {fields.map(({ key, header }) => (
                  <TableCell
                    key={String(key)}
                    onClick={() => handleSort(key)}
                    sx={{
                      cursor: "pointer",
                      fontWeight: "600",
                      textTransform: "capitalize",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {header}
                      {sort.field === key &&
                        (sort.direction === "asc" ? (
                          <ArrowUpward sx={{ fontSize: 15 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 15 }} />
                        ))}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={fields.length + 1} align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      py={4}
                      gap={1}
                    >
                      <CircularProgress
                        size={28}
                        color="inherit"
                        sx={{ color: COLORS.GOLD }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length > 0 ? (
                visibleRows.map((row) => {
                  const rowKey = getRowKey(row);
                  const refreshed = isRowRefreshed(row);
                  if (profileIdCheck) {
                    if ((row as any).isProfile) {
                      const group = getGroupKey(row);
                      const collapsed = !expandedGroups.has(group);
                      const groupSize = (row as any)[GROUP_SIZE_KEY] ?? 0;
                      return (
                        <TableRow
                          key={rowKey}
                          onClick={() => toggleGroup(group)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor: refreshed
                              ? REFRESHED_ROW_BG
                              : PROFILE_ROW_BG,
                            "&:hover": {
                              backgroundColor: refreshed
                                ? REFRESHED_ROW_HOVER_BG
                                : PROFILE_ROW_HOVER_BG,
                            },
                            "& td": {
                              borderBottom: `1px solid ${PROFILE_ROW_BORDER}`,
                              fontWeight: 500,
                            },
                          }}
                        >
                          <TableCell sx={{ width: "40px", py: 0.5 }}>
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              <KeyboardArrowDownIcon
                                fontSize="small"
                                sx={{
                                  color: COLORS.GOLD,
                                  transition: "transform 0.2s ease",
                                  transform: collapsed
                                    ? "rotate(-90deg)"
                                    : "rotate(0deg)",
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          {fields.map((field, fieldIndex) =>
                            fieldIndex === 0 ? (
                              <TableCell key={String(field.key)}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {String((row as any).name ?? "")}
                                  </Typography>
                                  <Box
                                    component="span"
                                    sx={{
                                      px: 0.75,
                                      py: 0.125,
                                      borderRadius: "10px",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      color: COLORS.GOLD,
                                      border: `1px solid ${PROFILE_ROW_BORDER}`,
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    {groupSize}{" "}
                                    {groupSize === 1 ? "account" : "accounts"}
                                  </Box>
                                </Box>
                              </TableCell>
                            ) : (
                              <AsyncTableCell
                                key={String(field.key)}
                                row={row}
                                field={field}
                              />
                            ),
                          )}
                        </TableRow>
                      );
                    } else {
                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            sx={{
                              backgroundColor: refreshed
                                ? REFRESHED_ROW_BG
                                : "#fff",
                              "&:hover": {
                                backgroundColor: refreshed
                                  ? REFRESHED_ROW_HOVER_BG
                                  : "#fafafa",
                              },
                            }}
                          >
                            <TableCell
                              onClick={
                                canViewMonitor
                                  ? () => handleRowExpand(rowKey, row)
                                  : undefined
                              }
                              sx={{
                                cursor: "pointer",
                                color: expandedRows.has(rowKey)
                                  ? COLORS.GOLD
                                  : undefined,
                                borderBottom: "none",
                                width: "40px",
                                borderLeft: `3px solid ${PROFILE_ROW_BORDER}`,
                                pl: 2,
                              }}
                            >
                              {renderExpandButton(rowKey, refreshed)}
                            </TableCell>
                            {fields.map((field) => (
                              <AsyncTableCell
                                key={String(field.key)}
                                row={row}
                                field={field}
                              />
                            ))}
                          </TableRow>
                          {expandedRows.has(rowKey) &&
                            expandedDetails[rowKey] && (
                              <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                                <TableCell
                                  colSpan={fields.length + 1}
                                  sx={{ borderBottom: "none", maxWidth: 0 }}
                                >
                                  <ExpandedDetailsTable
                                    details={expandedDetails[rowKey]!}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                        </React.Fragment>
                      );
                    }
                  }
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow
                        sx={
                          refreshed
                            ? {
                                backgroundColor: REFRESHED_ROW_BG,
                                "&:hover": {
                                  backgroundColor: REFRESHED_ROW_HOVER_BG,
                                },
                              }
                            : undefined
                        }
                      >
                        <TableCell
                          onClick={
                            canViewMonitor
                              ? () => handleRowExpand(rowKey, row)
                              : undefined
                          }
                          sx={{
                            cursor: "pointer",
                            color: expandedRows.has(rowKey)
                              ? COLORS.GOLD
                              : undefined,
                            borderBottom: "none",
                            width: "40px",
                          }}
                        >
                          {renderExpandButton(rowKey, refreshed)}
                        </TableCell>
                        {fields.map((field) => (
                          <AsyncTableCell
                            key={String(field.key)}
                            row={row}
                            field={field}
                          />
                        ))}
                      </TableRow>
                      {expandedRows.has(rowKey) && expandedDetails[rowKey] && (
                        <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                          <TableCell
                            colSpan={fields.length + 1}
                            sx={{ borderBottom: "none", maxWidth: 0 }}
                          >
                            <ExpandedDetailsTable
                              details={expandedDetails[rowKey]!}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={fields.length + 1} align="center">
                    <Typography>No Data Available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          rowGap: 0.5,
          columnGap: { xs: 1, sm: 1.5 },
          px: { xs: 1, sm: 2 },
          py: { xs: 0.5, sm: 0.625 },
          mt: { xs: 1, sm: 1.5 },
          width: { xs: "auto", sm: "100%" },
          mx: { xs: -2, sm: 0 },
          backgroundColor: COLORS.iconColor,
          border: `1px solid ${COLORS.dividerColor}`,
          borderRadius: "12px",
          boxShadow: "0 -6px 16px rgba(0,0,0,0.06)",
        }}
      >
        {[
          {
            label: "Count",
            value: <>{formatNumberWithCommas(summary.totalCount)}</>,
          },
          ...(
            [
              { label: "Total Converted Cost", field: "totalCost" },
              { label: "Total Rate", field: "totalRate" },
              { label: "Total Profit", field: "totalProfit" },
            ] as { label: string; field: MonetaryField }[]
          ).map(({ label, field }) => ({
            label,
            value: (
              <CurrencyTotalsValue
                byCurrency={summary.byCurrency}
                field={field}
              />
            ),
          })),
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              px: { xs: 1.25, sm: 1.5 },
              py: { xs: 0.375, sm: 0.5 },
              borderRadius: "8px",
              backgroundColor: COLORS.lightGrayBackground,
              minWidth: { xs: 0, sm: 130 },
            }}
          >
            <CustomLabel
              text={stat.label}
              fontWeight="600"
              color={COLORS.GOLD}
              sx={{
                fontSize: "0.625rem",
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                fontWeight: 700,
              }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <MessageTrackersDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        params={dialogParams}
        usedBy={usedBy}
      />
    </Box>
  );
};

interface AsyncTableCellProps<T> {
  row: T;
  field: IField<T>;
  onCellClick?: () => void;
  clickable?: boolean;
  firstColumn?: boolean;
}

const TransformTableCell = <T,>({
  row,
  field,
  onCellClick,
  clickable = false,
  firstColumn = false,
}: AsyncTableCellProps<T>) => {
  const [value, setValue] = useState<any>("-");

  // Memoize so rerenders that dont change the row or field don't refire the transform
  const transformResult = useMemo(
    () => (field.transform ? field.transform(row) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row, field],
  );

  useEffect(() => {
    if (transformResult === undefined) {
      setValue((row as any)[field.key] ?? "-");
      return;
    }
    Promise.resolve(transformResult).then(setValue);
  }, [transformResult, field.key, row]);

  return (
    <TableCell
      onClick={onCellClick}
      sx={{
        cursor: clickable ? "pointer" : "default",
        color: firstColumn ? COLORS.GOLD : undefined,
        borderBottom: "none",
      }}
    >
      {value}
    </TableCell>
  );
};

const AsyncTableCell = <T,>({
  row,
  field,
  onCellClick,
  clickable = false,
  firstColumn = false,
}: AsyncTableCellProps<T>) => {
  return field.options ? (
    <QueriedTableCell options={field.options(row)} firstColumn={firstColumn} />
  ) : (
    <TransformTableCell
      row={row}
      field={field}
      onCellClick={onCellClick}
      clickable={clickable}
      firstColumn={firstColumn}
    />
  );
};

interface QueriedTableCellProps {
  options: UseQueryOptions<any, any>;
  firstColumn?: boolean;
}

const QueriedTableCell: React.FC<QueriedTableCellProps> = ({
  options,
  firstColumn = false,
}) => {
  console.log("QueriedTableCell options:", options);
  const query = useQuery(options);
  console.log("QueriedTableCell query:", query);

  const getDisplayValue = (data: unknown): string => {
    if (Array.isArray(data)) {
      return data.map((item) => getDisplayValue(item)).join(", ");
    }
    if (typeof data === "object" && data !== null) {
      return Object.values(data)
        .map((value) => getDisplayValue(value))
        .join(", ");
    }
    return String(data);
  };

  return (
    <TableCell
      sx={{
        borderBottom: "none",
        color: firstColumn ? COLORS.GOLD : undefined,
      }}
    >
      {query.isLoading ? (
        <CircularProgress size={24} />
      ) : query.isError ? (
        "Error"
      ) : (
        getDisplayValue(query.data)
      )}
    </TableCell>
  );
};

export default LiveMonitorTable;
